import React from 'react';
import { Shield, CheckCircle, Layers, Wind } from 'lucide-react';

const CORE_STRENGTHS = [
  {
    icon: Shield,
    title: 'Machine Guarding Systems',
    description: 'Heavy-duty guarding systems designed to protect personnel and equipment in high-risk industrial environments.',
    features: [
      'Conveyor and rotating equipment guards',
      'Perimeter guarding and interlocked safety systems',
      'Modular and custom-fabricated guard designs',
      'Compliance with industrial ISO safety standards',
      'Designed for easy access and maintenance',
    ],
  },
  {
    icon: Wind,
    title: 'Dust Suppression Systems',
    description: 'Advanced dust control solutions to minimize airborne particles and improve plant conditions.',
    features: [
      'Dry fog and mist-based dust suppression systems',
      'Dust control at conveyor transfer points',
      'Reduced airborne dust and material loss',
      'Improved plant visibility and working conditions',
      'Supports environmental and safety compliance',
    ],
  },
  {
    icon: Layers,
    title: 'Wear Resistance Solutions',
    description: 'Engineered systems to reduce abrasion, erosion, and impact damage in material handling operations.',
    features: [
      'Ceramic lining and wear-resistant components',
      'Chute, hopper, and transfer point protection',
      'High-impact and high-abrasion resistant materials',
      'Reduced component replacement frequency',
      'Extended equipment and surface lifespan',
    ],
  },
];

const CoreStrengthsSection = () => (
  <section className="py-16 md:py-24 bg-white relative overflow-hidden">
    <div className="absolute inset-0 opacity-30">
      <div className="absolute top-0 left-0 w-64 h-64 bg-cyan-500/10 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-0 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl" />
    </div>

    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-16">
        <div className="inline-flex items-center space-x-2 px-4 py-2 bg-cyan-50 border border-cyan-200 rounded-full mb-4">
          <Shield className="h-4 w-4 text-cyan-600" />
          <span className="text-cyan-600 text-sm font-medium">Core Strengths</span>
        </div>
        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Why Choose Stallion Bulktech</h2>
        <p className="text-gray-600 max-w-2xl mx-auto">
          Machine guards engineered to extend equipment life, control dust, and cut downtime. Not just protect operators.
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-8">
        {CORE_STRENGTHS.map((item, index) => (
          <div key={item.title} className="group relative" style={{ animationDelay: `${index * 0.1}s` }}>
            <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-700" />

            <div className="relative h-full bg-gradient-to-br from-white to-gray-50/50 border border-gray-200 rounded-2xl p-8 hover:border-cyan-300 transition-all duration-500 transform hover:-translate-y-3 hover:shadow-2xl">
              <div className="relative w-16 h-16 mb-6">
                <div className="absolute inset-0 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl blur opacity-30 group-hover:opacity-60 transition-opacity animate-pulse" />
                <div className="relative w-16 h-16 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center transform group-hover:scale-110 group-hover:rotate-3 transition-all duration-500 shadow-lg">
                  <item.icon className="h-8 w-8 text-white" />
                </div>
              </div>

              <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-cyan-600 transition-colors">
                {item.title}
              </h3>
              <p className="text-gray-600 mb-6 leading-relaxed">{item.description}</p>

              <ul className="space-y-3">
                {item.features.map((feature) => (
                  <li key={feature} className="flex items-start space-x-3 group/item">
                    <CheckCircle className="h-5 w-5 text-cyan-600 flex-shrink-0 mt-0.5 group-hover/item:scale-110 transition-transform" />
                    <span className="text-sm text-gray-600">{feature}</span>
                  </li>
                ))}
              </ul>

              <div className="absolute top-6 right-6 w-20 h-20 bg-gradient-to-br from-cyan-400/10 to-blue-500/10 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-700" />
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export default CoreStrengthsSection;
